import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../api/client";
import { LinearProgress, Snackbar, Alert } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ q: "", page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const normalizedQuery = useMemo(
    () => (filters.q || "").trim().toLowerCase(),
    [filters.q]
  );

  function formatDate(iso) {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((p) => ({ ...p, q: searchText, page: 1 }));
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit]);

  async function runSearch(override = {}) {
    const merged = { ...filters, ...override };
    try {
      setLoading(true);
      const res = await adminApi.listUsers({
        role: "customer",
        includeProfile: false,
        page: Number(merged.page || 1),
        limit: Number(merged.limit || 20),
      });
      setCustomers(res?.items || []);
      setTotal(res?.total || 0);
      setFilters((prev) => ({ ...prev, ...override }));
    } catch (_e) {
      setCustomers([]);
      setTotal(0);
      const msg = _e?.data?.message || "Failed to load customers";
      setSnackbar({ open: true, message: msg });
    } finally {
      setLoading(false);
    }
  }

  const filteredCustomers = useMemo(() => {
    if (!normalizedQuery) return customers;
    return (customers || []).filter((c) => {
      const email = (c?.email || "").toLowerCase();
      const phone = (c?.phoneNumber || "").toLowerCase();
      return email.includes(normalizedQuery) || phone.includes(normalizedQuery);
    });
  }, [customers, normalizedQuery]);

  return (
    <div className="p-2 md:p-4">
      {/* Breadcrumb */}
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1 flex-wrap">
        <Link to="/dashboard" className="hover:text-gray-800">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4 opacity-60" />
        <span className="text-gray-800 font-medium">Customers</span>
      </div>

      {/* Search + Reset */}
      <div className="bg-white shadow rounded p-4 mb-4 flex flex-col md:flex-row gap-3">
        <input
          type="text"
          placeholder="Search email or phone"
          className="border p-2 rounded flex-1"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <button
          className="px-4 py-2 bg-gray-600 text-white rounded"
          onClick={() => {
            setSearchText("");
            setFilters({ q: "", page: 1, limit: filters.limit });
            runSearch({ page: 1 });
          }}
        >
          Reset
        </button>
      </div>

      {/* Table with scroll */}
      <div className="overflow-x-auto bg-white shadow rounded">
        <table className="min-w-full text-center">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Phone</th>
              <th className="p-2 border">Created</th>
              {/* <th className="p-2 border">Updated</th> */}
              <th className="p-2 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="p-2 border break-words">{c.email}</td>
                <td className="p-2 border">{c.phoneNumber || "-"}</td>
                <td className="p-2 border text-sm">
                  {formatDate(c?.createdAt)}
                </td>
                {/* <td className="p-2 border text-sm">{formatDate(c?.updatedAt)}</td> */}
                <td className="p-2 border">
                  <button
                    onClick={() => setSelectedRecord(c)}
                    className="px-3 py-1 bg-blue-500 text-white rounded"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {filteredCustomers.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500 text-center" colSpan={5}>
                  No customers
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 p-3">
        <div className="text-sm text-gray-600">Total: {total}</div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={filters.page <= 1}
            onClick={() =>
              runSearch({ page: Math.max(1, (filters.page || 1) - 1) })
            }
          >
            Prev
          </button>
          <span className="text-sm">Page {filters.page}</span>
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={customers.length < filters.limit}
            onClick={() => runSearch({ page: (filters.page || 1) + 1 })}
          >
            Next
          </button>
          <select
            className="ml-2 border p-1 rounded text-sm"
            value={filters.limit}
            onChange={(e) =>
              runSearch({ limit: Number(e.target.value), page: 1 })
            }
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      </div>

      {/* Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold mb-4">User Details</h3>
            <p>Email: {selectedRecord.email}</p>
            <p>Role: {selectedRecord.role}</p>
            <p>Phone: {selectedRecord.phoneNumber || "-"}</p>
            <button
              onClick={() => setSelectedRecord(null)}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Loader */}
      {loading && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <LinearProgress />
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: "" })}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
